import api from "./apiClient";
import {
  BarChart3,
  Building2,
  ClipboardList,
  CloudUpload,
  CreditCard,
  FileText,
  LayoutGrid,
  ListTodo,
  MapPin,
  MessageSquare,
  Package,
  Package2,
  QrCode,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  TableOfContents,
  Tag,
  Ticket,
  Truck,
  User,
  UserRoundCog,
  Users,
  Warehouse,
  Wrench,
} from "lucide-react";

import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/menus`;

/**
 * Ícones aceitos no campo `icon` do menu (nome exato do componente lucide-react).
 * Para usar um ícone novo cadastrado no backend, importe-o acima e adicione aqui.
 */
const ICONS = {
  BarChart3,
  Building2,
  ClipboardList,
  CloudUpload,
  CreditCard,
  FileText,
  LayoutGrid,
  ListTodo,
  MapPin,
  MessageSquare,
  Package,
  Package2,
  QrCode,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  TableOfContents,
  Tag,
  Ticket,
  Truck,
  User,
  UserRoundCog,
  Users,
  Warehouse,
  Wrench,
};

const DEFAULT_ICON = LayoutGrid;

/** Evento disparado após alterar menus/permissões, para a sidebar recarregar */
export const MENUS_CHANGED_EVENT = "menus:changed";

/**
 * Menu Service
 * Menu lateral do painel admin (MenuController - /menus).
 * GET /menus já devolve só os menus ativos que os perfis do usuário logado podem ver.
 */
class MenuService {
  /**
   * Menus visíveis para o usuário logado, ordenados por `order`
   * @returns {Promise<Array>} [{ id, label, icon, link, enabled, roles, order }]
   */
  async getMyMenus() {
    try {
      const response = await api.get(API_BASE);
      const menus = response.data?.data || response.data || [];
      return Array.isArray(menus)
        ? menus
            .filter((menu) => menu?.link && menu.enabled !== false)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [];
    } catch (error) {
      console.error("❌ Erro ao buscar menu do usuário:", error);
      throw error;
    }
  }

  /**
   * A API sempre devolve { data: [...] }, inclusive para um único registro
   * @param {object} response - Axios response
   * @returns {object|null}
   */
  extractOne(response) {
    const payload = response.data?.data ?? response.data;
    return Array.isArray(payload) ? (payload[0] ?? null) : payload;
  }

  /**
   * Listar todos os menus, inclusive inativos (admin), ordenados por `order`
   * @returns {Promise<Array>}
   */
  async getAllMenus() {
    try {
      const response = await api.get(
        `${API_BASE}/all`
      );
      const menus = response.data?.data || response.data || [];
      return Array.isArray(menus)
        ? [...menus].sort(
            (a, b) =>
              (a.order ?? 0) - (b.order ?? 0) || (a.id ?? 0) - (b.id ?? 0),
          )
        : [];
    } catch (error) {
      console.error("❌ Erro ao buscar todos os menus:", error);
      throw error;
    }
  }

  /**
   * Buscar menu por ID (admin)
   * @param {number} id - Menu ID
   * @returns {Promise<object>}
   */
  async getMenuById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return this.extractOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar menu ${id}:`, error);
      throw error;
    }
  }

  /**
   * Criar menu (admin)
   * @param {object} menuData - { label, icon, link, order, enabled, roles }
   * @returns {Promise<object>}
   */
  async createMenu(menuData) {
    try {
      const response = await api.post(
        API_BASE,
        menuData
      );
      return this.extractOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar menu:", error);
      throw error;
    }
  }

  /**
   * Atualizar menu (admin). O `link` não pode ser alterado.
   * @param {number} id - Menu ID
   * @param {object} menuData - { label, icon, link, order, enabled, roles }
   * @returns {Promise<object>}
   */
  async updateMenu(id, menuData) {
    try {
      const response = await api.put(
        `${API_BASE}/${id}`,
        menuData
      );
      return this.extractOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar menu ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remover menu e as permissões de perfil vinculadas (admin)
   * @param {number} id - Menu ID
   * @returns {Promise<void>}
   */
  async deleteMenu(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar menu ${id}:`, error);
      throw error;
    }
  }

  /**
   * Avisa a sidebar que menus/permissões mudaram
   */
  notifyMenusChanged() {
    window.dispatchEvent(new Event(MENUS_CHANGED_EVENT));
  }

  /**
   * Nomes de ícones disponíveis para o cadastro de menu
   * @returns {string[]}
   */
  getIconNames() {
    return Object.keys(ICONS).sort();
  }

  /**
   * Valida dados do menu (mesmas regras do MenuDTO)
   * @param {object} data
   * @returns {object} erros por campo
   */
  validateMenuData(data) {
    const errors = {};
    const label = String(data.label || "").trim();
    const icon = String(data.icon || "").trim();
    const link = String(data.link || "").trim();
    const order =
      data.order === "" || data.order === null ? NaN : Number(data.order);

    if (!label) errors.label = "Nome do menu é obrigatório";
    else if (label.length > 100) errors.label = "Máximo de 100 caracteres";

    if (!icon) errors.icon = "Ícone é obrigatório";

    if (!link) errors.link = "Link é obrigatório";
    else if (!/^[a-z0-9-]+$/.test(link))
      errors.link = "Use apenas letras minúsculas, números e hífen";
    else if (link.length > 100) errors.link = "Máximo de 100 caracteres";

    if (!Number.isInteger(order) || order < 0)
      errors.order = "Ordem deve ser um número inteiro maior ou igual a 0";

    return errors;
  }

  /**
   * Converte o nome do ícone vindo da API no componente lucide-react
   * @param {string} name - ex.: "BarChart3"
   * @returns {React.ComponentType}
   */
  resolveIcon(name) {
    const icon = ICONS[String(name || "").trim()];
    if (!icon && name) {
      console.warn(`⚠️ Ícone de menu não mapeado: ${name}`);
    }
    return icon || DEFAULT_ICON;
  }
}

// Export singleton instance
const menuService = new MenuService();
export default menuService;
